import { Inject, Injectable } from '@angular/core';
import { PartnerTrialService, PartnerTrialServiceInterface, SubscriptionLevel, TrialInterface } from '@galaxy/partner';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { daysRemaining, getTrialStatus, trialExpiredOver30DaysAgo, trialExpiring } from '../../partner-trial/utils';
import { TrialStatus } from './partner-trial-status';

export interface PartnerTrial {
  status: TrialStatus;
  subscriptionLevel: SubscriptionLevel;
  expiringSoon: boolean;
  expiredOver30DaysAgo: boolean;
  remainingDays: number;
  subscriptionTierId: string;
  start: Date;
  end: Date;
}

export interface PartnerTrialStore {
  partnerTrial$: Observable<PartnerTrial>;
  partnerHasTrial$: Observable<boolean>;
}

@Injectable({ providedIn: 'root' })
export class PartnerTrialStoreService implements PartnerTrialStore {
  private partnerTrial$$: ReplaySubject<TrialInterface> = new ReplaySubject(1);
  private partnerHasTrial$$: Subject<boolean> = new ReplaySubject(1);

  partnerHasTrial$: Observable<boolean> = this.partnerHasTrial$$.asObservable();
  partnerTrial$: Observable<PartnerTrial>;

  constructor(
    @Inject(PartnerTrialService) private partnerTrialService: PartnerTrialServiceInterface,
    @Inject('PARTNER_ID') private partnerId$: Observable<string>,
  ) {
    this.partnerTrial$ = this.partnerTrial$$.pipe(
      map((t) => ({
        status: getTrialStatus(t),
        remainingDays: daysRemaining(t.start, t.end),
        expiringSoon: trialExpiring(t),
        expiredOver30DaysAgo: trialExpiredOver30DaysAgo(t),
        subscriptionLevel: t.subscriptionLevel,
        subscriptionTierId: t.subscriptionTierId,
        start: t.start,
        end: t.end,
      })),
    );

    this.partnerId$
      .pipe(
        filter((partnerId) => !!partnerId),
        switchMap((partnerId) => this.partnerTrialService.getTrial(partnerId)),
      )
      .subscribe((trial) => this.handleTrialResponse(trial));
  }

  handleTrialResponse(trial: TrialInterface): void {
    const emptyTrial = Object.keys(trial).length === 0 && trial.constructor === Object;
    this.partnerHasTrial$$.next(!emptyTrial);
    this.partnerTrial$$.next(trial);
  }
}
